import * as Sentry from "@sentry/react";
import { useQuery } from "react-query";

import axios from "axios";

import { RigFracValuesResponse, safeParseRigFracValues } from "models/schemas";

const dataRoot = process.env.REACT_APP_DATA_RS_ROOT;

export const RIG_FRAC_VALUES_QUERY_KEY = "rigFracValues";

// The value domain changes at most daily, so hold it a long time before refetch.
const RIG_FRAC_VALUES_STALE_TIME = 1000 * 60 * 60; // 1 hour

export interface UseRigFracValuesQueryParams {
  enabled?: boolean;
  // Every stored value rather than only currently-active ones (EVA-338).
  historical?: boolean;
}

export interface UseRigFracValuesQueryResult {
  data: Maybe<RigFracValuesResponse>;
  isFetching: boolean;
  error: unknown;
}

/**
 * Fetches the rig/frac attribute value domains. A parse failure returns null
 * (not a throw) so consumers fall back to the layer's static color rather than
 * crashing; a network failure surfaces via `error` with `data` undefined —
 * both signal the same static-color fallback (FR-012).
 */
export function useRigFracValuesQuery(
  params: UseRigFracValuesQueryParams = {}
): UseRigFracValuesQueryResult {
  const { enabled = true, historical = false } = params;

  const { data, isFetching, error } = useQuery<RigFracValuesResponse | null>(
    historical ? [RIG_FRAC_VALUES_QUERY_KEY, "historical"] : [RIG_FRAC_VALUES_QUERY_KEY],
    async () => {
      const url = `${dataRoot}/api/v1/rigfrac/values`;
      const response = historical
        ? await axios.get(url, { params: { historical: true } })
        : await axios.get(url);
      const parsed = safeParseRigFracValues(response.data);
      if (!parsed.success) {
        // Contract drift between this client and the data service must be
        // visible — surface it as a warning rather than failing silently.
        Sentry.withScope((scope) => {
          scope.setLevel("warning");
          scope.setContext("rigFracValues", { issues: parsed.error.issues });
          Sentry.captureMessage("Rig/frac values response failed schema validation");
        });
        return null;
      }
      return parsed.data;
    },
    {
      enabled,
      staleTime: RIG_FRAC_VALUES_STALE_TIME,
      refetchOnWindowFocus: false
    }
  );

  return { data: data ?? undefined, isFetching, error };
}
